/**
 * Clipper integration for the BIM viewer.
 * Uses @thatopen/components Clipper + Raycasters (already in v3.2.0, no upgrade).
 *
 * Behaviour:
 * - Double-click on any model surface in the viewport → creates a clipping plane.
 * - Press Delete or Backspace → deletes the clipping plane under the cursor.
 * - A small floating control panel (top-left of container) toggles the
 *   clipper on/off, planes visibility, color, opacity, size, and "Delete All".
 *
 * Public API:
 *   const clipper = setupClipper(components, world, container);
 *   clipper.dispose();
 */
import * as THREE from "three";
import * as OBC from "@thatopen/components";
import * as FRAGS from "@thatopen/fragments";

interface SetupClipperResult {
  clipper: OBC.Clipper;
  panel: HTMLElement;
  togglePanel(): void;
  setPanelVisible(visible: boolean): void;
  isPanelVisible(): boolean;
  dispose(): void;
}

export function setupClipper(
  components: OBC.Components,
  world: any, // OBC.World — kept loose to avoid generics friction with caller
  container: HTMLElement,
  fragments: FRAGS.FragmentsModels
): SetupClipperResult {
  // 1. Register a raycaster for the world (used by Clipper.delete/pick on
  //    its own plane meshes — those ARE in world.scene so SimpleRaycaster
  //    handles them fine).
  const casters = components.get(OBC.Raycasters);
  casters.get(world);

  // 2. Get the singleton Clipper.
  const clipper = components.get(OBC.Clipper);
  clipper.enabled = true;

  // 3. Wire fragment models to use the renderer's clipping planes.
  //    FragmentsModel does its own GPU-side clipping in a worker thread;
  //    standard material.clippingPlanes is ignored. We must hand each model
  //    a callback returning the live planes array.
  const renderer = world.renderer.three as THREE.WebGLRenderer;
  renderer.localClippingEnabled = true;

  const planesGetter = (): THREE.Plane[] => (renderer.clippingPlanes as THREE.Plane[]) || [];
  const wireModelClipping = (model: any) => {
    try {
      model.getClippingPlanesEvent = planesGetter;
    } catch (err) {
      console.warn("[clipper] failed to wire clipping planes on model", err);
    }
  };

  for (const [, model] of fragments.models.list) wireModelClipping(model);
  fragments.models.list.onItemSet.add(({ value: model }: any) => wireModelClipping(model));

  const refreshFragments = () => {
    try { fragments.update(true); } catch (err) { /* ignore */ }
  };

  // 3. Double-click → create a clipping plane at the surface under the cursor.
  //    NOTE: we cannot use clipper.create(world) directly because the viewer
  //    loads models via FRAGS.FragmentsModels (not OBC.FragmentsManager), so
  //    OBC's SimpleRaycaster cannot pick fragment surfaces. We raycast each
  //    fragment model ourselves and feed the hit into
  //    createFromNormalAndCoplanarPoint().
  const reusableMouse = new THREE.Vector2();
  let lastDblClickTs = 0;

  const onDblClick = async (event: MouseEvent) => {
    // Dedupe: handler may be bound to both canvas and container; both fire
    // on bubble. Ignore secondary call within 50 ms.
    const now = performance.now();
    if (now - lastDblClickTs < 50) return;
    lastDblClickTs = now;

    // Gate: only create planes when the clipper panel is OPEN. Keeps
    // double-click "free" for other uses (zoom-to-fit, etc.) and stops
    // accidental clipping when the panel isn't on screen. Existing planes
    // continue to render — we only block creation.
    if (panel.style.display === "none") {
      console.log("[clipper] dblclick ignored — clipper panel is hidden");
      return;
    }
    console.log("[clipper] dblclick fired", {
      enabled: clipper.enabled,
      panelVisible: panel.style.display !== "none",
      target: (event.target as HTMLElement)?.tagName,
      clientX: event.clientX,
      clientY: event.clientY,
      modelCount: fragments.models.list.size,
    });
    if (!clipper.enabled) return;
    try {
      const camera = world.camera.three as THREE.Camera;
      const dom = world.renderer.three.domElement as HTMLCanvasElement;

      // Fragments expects a Vector2 with raw clientX/Y (it computes NDC internally
      // using the canvas getBoundingClientRect). Match the viewer's existing pattern.
      reusableMouse.x = event.clientX;
      reusableMouse.y = event.clientY;

      type Hit = { point: THREE.Vector3; normal: THREE.Vector3; distance: number; modelId: string };
      let best: Hit | null = null;
      const camPos = (camera as any).position as THREE.Vector3;

      for (const [modelId, model] of fragments.models.list) {
        try {
          const r: any = await (model as any).raycast({
            camera,
            mouse: reusableMouse,
            dom,
          });
          if (!r) {
            console.log(`[clipper]   - ${modelId}: miss`);
            continue;
          }
          if (!r.point) {
            console.log(`[clipper]   - ${modelId}: hit but no .point`, r);
            continue;
          }
          const point = new THREE.Vector3(r.point.x, r.point.y, r.point.z);
          let normal: THREE.Vector3 | null = null;
          if (r.normal) {
            normal = new THREE.Vector3(r.normal.x, r.normal.y, r.normal.z).normalize();
          } else if (r.face?.normal) {
            normal = new THREE.Vector3(r.face.normal.x, r.face.normal.y, r.face.normal.z).normalize();
          }
          if (!normal) {
            console.log(`[clipper]   - ${modelId}: hit but no normal`, r);
            continue;
          }
          const dist = camPos ? camPos.distanceTo(point) : 0;
          console.log(`[clipper]   - ${modelId}: HIT dist=${dist.toFixed(2)} pt=(${point.x.toFixed(2)},${point.y.toFixed(2)},${point.z.toFixed(2)})`);
          if (!best || dist < best.distance) {
            best = { point, normal, distance: dist, modelId };
          }
        } catch (err) {
          console.warn(`[clipper]   - ${modelId}: raycast threw`, err);
        }
      }

      if (!best) {
        console.warn("[clipper] no fragment hit under cursor — plane not created");
        return;
      }

      // Flip the surface normal so it points AWAY from the camera. THREE
      // clips the *negative* side of the plane, so:
      //   normal pointing away from camera → camera side is the negative
      //   side → camera side gets cut away → user sees what's behind
      //   the surface they clicked.
      // This is the intuitive "click here to slice into the model"
      // behaviour, regardless of how the underlying mesh winding stored
      // the surface normal.
      const cam2hit = best.point.clone().sub(camPos).normalize();
      const planeNormal = best.normal.clone();
      if (planeNormal.dot(cam2hit) < 0) {
        planeNormal.negate();
      }

      console.log(`[clipper] creating plane from model=${best.modelId}`, {
        point: best.point.toArray(),
        surfaceNormal: best.normal.toArray(),
        planeNormal: planeNormal.toArray(),
        flipped: !planeNormal.equals(best.normal),
      });
      const id = clipper.createFromNormalAndCoplanarPoint(world, planeNormal, best.point);
      console.log(`[clipper] plane created, id=${id}, total planes=${clipper.list.size}`);

      // Force a fragments redraw so the worker re-applies clipping
      try { fragments.update(true); } catch { /* noop */ }
    } catch (err) {
      console.error("[clipper] create failed:", err);
    }
  };

  // Bind to BOTH the container and the canvas. The renderer canvas often
  // sits inside container; binding to canvas in capture phase guarantees we
  // see the dblclick even if some inner element calls stopPropagation.
  container.addEventListener("dblclick", onDblClick);
  const canvas = world.renderer.three.domElement as HTMLCanvasElement;
  if (canvas && canvas !== container) {
    canvas.addEventListener("dblclick", onDblClick);
  }

  // 4. Delete / Backspace → delete the plane under the cursor.
  //    Skip when typing in an input, otherwise we'd swallow user edits.
  const onKeyDown = (event: KeyboardEvent) => {
    if (!clipper.enabled) return;
    const tag = (document.activeElement?.tagName || "").toLowerCase();
    if (tag === "input" || tag === "textarea" || (document.activeElement as HTMLElement)?.isContentEditable) return;
    if (event.code === "Delete" || event.code === "Backspace") {
      clipper.delete(world).catch((err) => {
        console.warn("[clipper] delete failed:", err);
      });
    }
  };
  window.addEventListener("keydown", onKeyDown);

  // 5. Floating control panel — draggable, toggled from the bottom toolbar.
  //    Vanilla DOM so we don't pull in @thatopen/ui-obc (avoids dep upgrade).
  //    Style matches the existing native panels (Selected Element Info,
  //    Model Information) so all three look uniform.
  const panel = document.createElement("div");
  panel.id = "clipper-panel";
  panel.style.cssText = `
    position: fixed;
    top: 80px;
    left: 12px;
    width: 14rem;
    z-index: 9999;
    background: rgba(10, 14, 39, 0.95);
    border: 1px solid #44d3de;
    border-radius: 8px;
    box-shadow: 0 0 20px rgba(68, 211, 222, 0.3);
    backdrop-filter: blur(10px);
    color: #e0e0e0;
    font: 12px -apple-system, 'Segoe UI', Helvetica, sans-serif;
    pointer-events: auto;
    user-select: none;
    overflow: hidden;
    display: none;
    flex-direction: column;
  `;

  // Helper to build a labelled row
  const row = (labelText: string, control: HTMLElement) => {
    const r = document.createElement("div");
    r.style.cssText = `
      display: flex; align-items: center; justify-content: space-between;
      gap: 8px; margin: 4px 0;
    `;
    const lbl = document.createElement("span");
    lbl.textContent = labelText;
    lbl.style.cssText = "color: #cfeef0; font-size: 11px;";
    r.appendChild(lbl);
    r.appendChild(control);
    return r;
  };

  // Forward declaration — defined further below; TS otherwise complains
  // about closeBtn / drag listeners referencing them.
  let setPanelVisible: (visible: boolean) => void = () => {};

  // Drag handle / header
  const header = document.createElement("div");
  header.id = "clipper-panel-header";
  header.setAttribute("data-panel-header", "true");
  header.style.cssText = `
    padding: 0.5rem;
    border-bottom: 1px solid rgba(68, 211, 222, 0.3);
    color: #44d3de;
    font-weight: 600;
    font-size: 0.75rem;
    cursor: move;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  `;

  const titleSpan = document.createElement("span");
  titleSpan.textContent = "Clipper";
  header.appendChild(titleSpan);

  const closeBtn = document.createElement("button");
  closeBtn.innerHTML = "&times;";
  closeBtn.title = "Close panel";
  closeBtn.style.cssText = `
    background: transparent; border: none; color: #44d3de;
    cursor: pointer; font-size: 16px; line-height: 1;
    padding: 0 4px; border-radius: 4px;
  `;
  closeBtn.onmouseenter = () => { closeBtn.style.background = "rgba(255, 255, 255, 0.12)"; };
  closeBtn.onmouseleave = () => { closeBtn.style.background = "transparent"; };
  closeBtn.addEventListener("click", (e) => { e.stopPropagation(); setPanelVisible(false); });
  header.appendChild(closeBtn);
  panel.appendChild(header);

  const body = document.createElement("div");
  body.id = "clipper-panel-body";
  body.style.cssText = `
    flex: 1;
    overflow: auto;
    padding: 0.5rem;
    font-size: 0.65rem;
    color: #e0e0e0;
  `;
  panel.appendChild(body);

  // Drag from the header. Built-in (mirrors makePanelDraggable in viewer.ts
  // but scoped to this module so the panel works regardless of init order).
  let dragging = false;
  let dragOffsetX = 0;
  let dragOffsetY = 0;

  header.addEventListener("mousedown", (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target === closeBtn || closeBtn.contains(target)) return;
    const rect = panel.getBoundingClientRect();
    dragOffsetX = e.clientX - rect.left;
    dragOffsetY = e.clientY - rect.top;
    dragging = true;
    header.style.cursor = "grabbing";
    e.preventDefault();
    e.stopPropagation();
  });
  const onPanelDrag = (e: MouseEvent) => {
    if (!dragging) return;
    e.preventDefault();
    const w = panel.offsetWidth;
    const h = panel.offsetHeight;
    const maxX = window.innerWidth - 40;
    const maxY = window.innerHeight - 40;
    let nx = e.clientX - dragOffsetX;
    let ny = e.clientY - dragOffsetY;
    nx = Math.max(0 - w + 40, Math.min(nx, maxX));
    ny = Math.max(0, Math.min(ny, maxY));
    panel.style.left = `${nx}px`;
    panel.style.top = `${ny}px`;
    panel.style.right = "auto";
    panel.style.bottom = "auto";
  };
  const onPanelDragEnd = () => {
    if (dragging) {
      dragging = false;
      header.style.cursor = "grab";
    }
  };
  document.addEventListener("mousemove", onPanelDrag);
  document.addEventListener("mouseup", onPanelDragEnd);

  // Hint
  const hint = document.createElement("div");
  hint.style.cssText = "color: #8db5b9; font-size: 10.5px; margin-bottom: 6px; line-height: 1.4;";
  hint.innerHTML = "Double-click model → create plane<br/>Delete key → remove plane under cursor";
  body.appendChild(hint);

  // Enable toggle
  const enableCb = document.createElement("input");
  enableCb.type = "checkbox";
  enableCb.checked = true;
  enableCb.addEventListener("change", () => {
    clipper.config.enabled = enableCb.checked;
  });
  body.appendChild(row("Enabled", enableCb));

  // Visible toggle
  const visibleCb = document.createElement("input");
  visibleCb.type = "checkbox";
  visibleCb.checked = true;
  visibleCb.addEventListener("change", () => {
    clipper.config.visible = visibleCb.checked;
  });
  body.appendChild(row("Plane visible", visibleCb));

  // ---- Per-plane / Default style block ----
  // The Color and Opacity controls below operate in TWO modes:
  //   * No plane selected → they edit `clipper.config.*` (the defaults
  //     used for any plane created from this point on).
  //   * A plane is selected (click its row in the list below) → they
  //     edit just that plane's helper material in place. Other planes
  //     and the global defaults are untouched.
  //
  // The scopeLabel element makes the current target visually obvious.
  let selectedPlaneId: string | null = null;
  const scopeLabel = document.createElement("div");
  scopeLabel.style.cssText = `
    color: #8db5b9;
    font-size: 10.5px;
    margin: 2px 0 4px;
    padding: 3px 6px;
    background: rgba(68, 211, 222, 0.06);
    border: 1px dashed rgba(68, 211, 222, 0.25);
    border-radius: 4px;
  `;
  body.appendChild(scopeLabel);

  const colorInput = document.createElement("input");
  colorInput.type = "color";
  colorInput.value = "#44d3de";
  colorInput.style.cssText = "width: 40px; height: 22px; border: none; background: transparent; cursor: pointer;";

  const opacityInput = document.createElement("input");
  opacityInput.type = "range";
  opacityInput.min = "0.05";
  opacityInput.max = "1";
  opacityInput.step = "0.05";
  opacityInput.value = "0.25";
  opacityInput.style.cssText = "width: 110px; accent-color: #44d3de;";

  // OBC.SimplePlane structure:
  //   plane.helper                = THREE.Object3D (container, no material)
  //   plane.helper.children[0]    = THREE.Mesh _planeMesh (the visible square)
  //   plane.planeMaterial         = _planeMesh.material (getter)
  // ALL planes share `clipper._material` by default (passed into every
  // SimplePlane constructor). To get per-plane colour / opacity we have
  // to clone the material on first edit and assign it back via the
  // `planeMaterial` setter (which also updates _planeMesh.material).
  const getPlaneMaterial = (plane: any): any | null => {
    try {
      const mat = plane?.planeMaterial;
      if (Array.isArray(mat)) return mat[0] || null;
      return mat || null;
    } catch {
      return null;
    }
  };

  const ensureOwnMaterial = (plane: any): any | null => {
    if (!plane) return null;
    if (plane.__hasOwnMaterial) return getPlaneMaterial(plane);
    const shared = getPlaneMaterial(plane);
    if (!shared || typeof shared.clone !== "function") return shared;
    try {
      const cloned = shared.clone();
      cloned.transparent = true; // we'll be touching opacity, force on
      plane.planeMaterial = cloned;
      plane.__hasOwnMaterial = true;
      return cloned;
    } catch (err) {
      console.warn("[clipper] ensureOwnMaterial clone failed", err);
      return shared;
    }
  };

  const applyStyleToPlane = (plane: any, hexColor: string, opacity: number) => {
    const mat = ensureOwnMaterial(plane);
    if (!mat) return;
    try {
      if (mat.color) mat.color.set(hexColor);
      mat.opacity = opacity;
      mat.transparent = opacity < 1;
      mat.needsUpdate = true;
    } catch (err) {
      console.warn("[clipper] applyStyleToPlane failed", err);
    }
  };

  const updateScopeLabel = () => {
    if (selectedPlaneId && clipper.list.has(selectedPlaneId)) {
      let idx = 0;
      let n = 0;
      for (const [pid] of clipper.list) {
        if (pid === selectedPlaneId) { idx = n; break; }
        n++;
      }
      scopeLabel.innerHTML =
        `<span style="color:#44d3de">Editing:</span> <b>Plane ${idx + 1}</b> ` +
        `<span style="color:#688080">(click row again to deselect)</span>`;
    } else {
      scopeLabel.innerHTML =
        `<span style="color:#44d3de">Default style</span> ` +
        `<span style="color:#688080">(applies to NEW planes — click a plane row to edit it)</span>`;
    }
  };

  // Sync the colour + opacity inputs to the currently-targeted plane (or
  // global defaults). Called on selection change so the user sees the
  // values they're actually editing.
  const syncStyleInputsFromTarget = () => {
    if (selectedPlaneId && clipper.list.has(selectedPlaneId)) {
      const plane: any = clipper.list.get(selectedPlaneId);
      const mat = getPlaneMaterial(plane);
      if (mat) {
        try {
          if (mat.color) colorInput.value = `#${mat.color.getHexString()}`;
        } catch { /* noop */ }
        if (typeof mat.opacity === "number") {
          opacityInput.value = String(Math.max(0.05, Math.min(1, mat.opacity)));
        }
      }
    } else {
      try {
        if (clipper.config.color) {
          colorInput.value = `#${(clipper.config.color as THREE.Color).getHexString()}`;
        }
      } catch { /* noop */ }
      if (typeof clipper.config.opacity === "number") {
        opacityInput.value = String(clipper.config.opacity);
      }
    }
  };

  colorInput.addEventListener("input", () => {
    if (selectedPlaneId && clipper.list.has(selectedPlaneId)) {
      const plane: any = clipper.list.get(selectedPlaneId);
      applyStyleToPlane(plane, colorInput.value, parseFloat(opacityInput.value));
    } else {
      try { clipper.config.color = new THREE.Color(colorInput.value); } catch { /* noop */ }
    }
    refreshSwatches();
  });
  opacityInput.addEventListener("input", () => {
    const o = parseFloat(opacityInput.value);
    if (selectedPlaneId && clipper.list.has(selectedPlaneId)) {
      const plane: any = clipper.list.get(selectedPlaneId);
      applyStyleToPlane(plane, colorInput.value, o);
    } else {
      try { clipper.config.opacity = o; } catch { /* noop */ }
    }
    refreshSwatches();
  });
  // Apply initial defaults so first auto-created plane uses cyan @ 25%
  try { clipper.config.color = new THREE.Color(colorInput.value); } catch { /* noop */ }
  try { clipper.config.opacity = parseFloat(opacityInput.value); } catch { /* noop */ }

  body.appendChild(row("Color", colorInput));
  body.appendChild(row("Opacity", opacityInput));

  // Size slider — covers small detail to whole-building scale.
  // Default = MAX so newly created planes are big enough to fully cut
  // through any model out of the box. User can drag down for finer
  // control on small parts.
  const sizeInput = document.createElement("input");
  sizeInput.type = "range";
  sizeInput.min = "1";
  sizeInput.max = "500";
  sizeInput.step = "1";
  sizeInput.value = sizeInput.max;
  sizeInput.style.cssText = "width: 110px; accent-color: #44d3de;";
  const applySize = (size: number) => {
    try { clipper.config.size = size; } catch { /* noop */ }
    // clipper.config.size only affects FUTURE planes — also resize existing ones.
    for (const [, plane] of clipper.list) {
      try { (plane as any).size = size; } catch { /* noop */ }
    }
  };
  sizeInput.addEventListener("input", () => {
    applySize(parseFloat(sizeInput.value));
  });
  applySize(parseFloat(sizeInput.value));
  body.appendChild(row("Size", sizeInput));

  // Gizmo mode: Move (default) ↔ Rotate
  // SimplePlane ships with TransformControls in TRANSLATE mode, Z axis only.
  // To control plane orientation we flip it to ROTATE mode and enable X/Y handles.
  // After a rotation, we manually recompute the plane's normal from the helper's
  // quaternion (helper's local +Z = plane normal), since SimplePlane does not.
  const modeWrap = document.createElement("div");
  modeWrap.style.cssText = "display: flex; gap: 4px; margin-top: 4px;";

  const makeModeBtn = (label: string, value: "translate" | "rotate") => {
    const b = document.createElement("button");
    b.textContent = label;
    b.dataset.mode = value;
    b.style.cssText = `
      flex: 1;
      padding: 5px 8px;
      background: rgba(68, 211, 222, 0.08);
      color: #cfeef0;
      border: 1px solid rgba(68, 211, 222, 0.35);
      border-radius: 4px;
      cursor: pointer;
      font-size: 11px;
      transition: background 120ms ease, color 120ms ease;
    `;
    return b;
  };

  const moveBtn = makeModeBtn("Move", "translate");
  const rotateBtn = makeModeBtn("Rotate", "rotate");
  modeWrap.appendChild(moveBtn);
  modeWrap.appendChild(rotateBtn);
  body.appendChild(row("Gizmo", modeWrap));

  let currentMode: "translate" | "rotate" = "translate";

  const paintModeButtons = () => {
    for (const b of [moveBtn, rotateBtn]) {
      const active = b.dataset.mode === currentMode;
      b.style.background = active ? "#44d3de" : "rgba(68, 211, 222, 0.08)";
      b.style.color = active ? "#0a1422" : "#cfeef0";
      b.style.fontWeight = active ? "600" : "normal";
    }
  };

  // Recompute plane.normal from helper.quaternion after a rotate, then push
  // the new plane to the renderer so fragments worker re-clips with it.
  const syncPlaneFromHelper = (plane: any) => {
    try {
      const helper: THREE.Object3D = plane.helper;
      if (!helper) return;
      const newNormal = new THREE.Vector3(0, 0, 1)
        .applyQuaternion(helper.quaternion)
        .normalize();
      plane.normal.copy(newNormal);
      plane.three.setFromNormalAndCoplanarPoint(newNormal, helper.position);
    } catch (err) {
      console.warn("[clipper] syncPlaneFromHelper failed", err);
    }
  };

  // Strip the renderer's clipping planes from a sub-tree's materials so the
  // TransformControls gizmo (rotate rings, translate arrows, picker meshes)
  // is NEVER clipped by the very plane it controls. We set
  // material.clippingPlanes = [] which means "this material uses no clipping
  // planes", overriding the renderer's global default.
  const exemptFromClipping = (root: THREE.Object3D | null | undefined) => {
    if (!root) return;
    root.traverse((child: any) => {
      const m = child.material;
      if (!m) return;
      const apply = (mat: any) => {
        try {
          mat.clippingPlanes = [];
          mat.clipShadows = false;
        } catch { /* noop */ }
      };
      if (Array.isArray(m)) m.forEach(apply);
      else apply(m);
    });
  };

  // Per-frame enforcement. TransformControls swaps gizmo materials when the
  // mode changes (translate ↔ rotate ↔ scale), and three.js may also rebuild
  // internal helper meshes. Re-applying the exemption every frame guarantees
  // the gizmo never gets clipped no matter what the lib does internally.
  const enforceGizmoClippingExemption = () => {
    for (const [, plane] of clipper.list) {
      const tc: any = (plane as any)._controls;
      if (tc) {
        try {
          const helper = typeof tc.getHelper === "function" ? tc.getHelper() : tc;
          exemptFromClipping(helper);
          // Also dig into private fields the lib uses (_root, _gizmo, _plane)
          exemptFromClipping(tc._root);
          exemptFromClipping(tc._gizmo);
          exemptFromClipping(tc._plane);
        } catch { /* noop */ }
      }
      exemptFromClipping((plane as any).helper);
    }
  };
  try {
    if (world.renderer && world.renderer.onBeforeUpdate) {
      world.renderer.onBeforeUpdate.add(enforceGizmoClippingExemption);
    }
  } catch (err) {
    console.warn("[clipper] could not hook onBeforeUpdate, gizmo clipping may flicker", err);
  }

  const applyModeToPlane = (plane: any) => {
    try {
      const tc = plane._controls;
      if (!tc) return;
      tc.setMode(currentMode);
      if (currentMode === "rotate") {
        tc.showX = true;
        tc.showY = true;
        tc.showZ = true;
      } else {
        // Restore SimplePlane default: only Z translate
        tc.showX = false;
        tc.showY = false;
        tc.showZ = true;
      }
      // Make sure the gizmo itself is not chopped by its own clipping plane.
      // TransformControls in newer three.js uses .getHelper(); the helper
      // sub-tree contains all gizmo meshes (arrows, rings, pickers).
      try {
        const helper = typeof tc.getHelper === "function" ? tc.getHelper() : tc;
        exemptFromClipping(helper);
      } catch { /* noop */ }
      // Also exempt the plane's own helper (the visual square) so dragging
      // along the normal doesn't make the plane visually disappear.
      exemptFromClipping(plane.helper);
      // Hook objectChange once per plane to keep clipping plane in sync
      // with the gizmo (translation along Z OR rotation around X/Y).
      if (!tc.__clipperHooked) {
        tc.addEventListener("objectChange", () => {
          syncPlaneFromHelper(plane);
          refreshFragments();
        });
        tc.__clipperHooked = true;
      }
    } catch (err) {
      console.warn("[clipper] applyModeToPlane failed", err);
    }
  };

  const applyModeToAll = () => {
    for (const [, plane] of clipper.list) applyModeToPlane(plane);
    paintModeButtons();
    refreshFragments();
  };

  moveBtn.addEventListener("click", () => {
    currentMode = "translate";
    applyModeToAll();
  });
  rotateBtn.addEventListener("click", () => {
    currentMode = "rotate";
    applyModeToAll();
  });

  // New planes inherit the active mode and the hook
  clipper.onAfterCreate.add((plane: any) => applyModeToPlane(plane));

  // Force every newly-created plane to take the current slider size and
  // make sure its (shared) material is transparent — Clipper's
  // `createFromNormalAndCoplanarPoint` builds the SimplePlane with the
  // constructor-default size of 5 (it does not consult clipper.config.size,
  // unlike `create()`), so without this hook our size slider would have
  // no visible effect on planes spawned via double-click.
  clipper.onAfterCreate.add((plane: any) => {
    try {
      const sz = parseFloat(sizeInput.value);
      if (Number.isFinite(sz)) plane.size = sz;
    } catch { /* noop */ }
    // Make sure the shared material renders translucent, otherwise opacity
    // tweaks via the slider have no visible effect.
    try {
      const mat = getPlaneMaterial(plane);
      if (mat) {
        const targetOp = parseFloat(opacityInput.value);
        if (Number.isFinite(targetOp)) {
          mat.opacity = targetOp;
          mat.transparent = targetOp < 1;
        } else {
          mat.transparent = (mat.opacity ?? 1) < 1;
        }
        mat.needsUpdate = true;
      }
    } catch { /* noop */ }
  });

  paintModeButtons();

  // Delete-all button
  const deleteAllBtn = document.createElement("button");
  deleteAllBtn.textContent = "Delete All Planes";
  deleteAllBtn.style.cssText = `
    width: 100%;
    margin-top: 8px;
    padding: 6px 10px;
    background: rgba(255, 80, 80, 0.18);
    color: #ff8a8a;
    border: 1px solid rgba(255, 80, 80, 0.45);
    border-radius: 4px;
    cursor: pointer;
    font-size: 11.5px;
    transition: background 120ms ease;
  `;
  deleteAllBtn.onmouseenter = () => {
    deleteAllBtn.style.background = "rgba(255, 80, 80, 0.32)";
  };
  deleteAllBtn.onmouseleave = () => {
    deleteAllBtn.style.background = "rgba(255, 80, 80, 0.18)";
  };
  deleteAllBtn.addEventListener("click", () => {
    // Snapshot first because forceRemovePlane mutates the live map
    const snapshot: Array<[string, any]> = [];
    for (const [id, p] of clipper.list) snapshot.push([id, p]);
    for (const [id, p] of snapshot) forceRemovePlane(id, p);
    rebuildList();
    refreshFragments();
  });
  body.appendChild(deleteAllBtn);

  // ---------- Plane list section ----------
  // forceRemovePlane: bullet-proof delete that:
  //   1. removes plane from clipper.list (via .delete which triggers
  //      onBeforeDelete → plane.dispose);
  //   2. if step 1 throws or fails to clean up, manually walks the renderer
  //      and scene to remove every trace of the plane (so it can never come
  //      back as a phantom row).
  const forceRemovePlane = (planeId: string, plane: any) => {
    try {
      clipper.list.delete(planeId);
    } catch (err) {
      console.warn("[clipper] clipper.list.delete threw, doing manual cleanup", err);
    }
    // Defensive: force-remove from renderer's clipping planes
    try {
      const rendererThree = (world.renderer.three as THREE.WebGLRenderer);
      if (plane?.three && Array.isArray(rendererThree.clippingPlanes)) {
        const arr = rendererThree.clippingPlanes;
        const idx = arr.indexOf(plane.three);
        if (idx > -1) arr.splice(idx, 1);
      }
      if (typeof world.renderer.setPlane === "function" && plane?.three) {
        try { world.renderer.setPlane(false, plane.three); } catch { /* noop */ }
      }
    } catch { /* noop */ }
    // Defensive: remove helper + transform-controls helper from scene
    try {
      if (plane?.helper && plane.helper.parent) {
        plane.helper.parent.remove(plane.helper);
      }
      const tc: any = plane?._controls;
      if (tc) {
        const helper = typeof tc.getHelper === "function" ? tc.getHelper() : null;
        if (helper && helper.parent) helper.parent.remove(helper);
        if (tc._root && tc._root.parent) tc._root.parent.remove(tc._root);
      }
    } catch { /* noop */ }
    // Defensive: in case lib didn't remove from the map after error
    try {
      if (clipper.list.has(planeId)) {
        (clipper.list as any).set?.(planeId, undefined);
        clipper.list.delete(planeId);
      }
    } catch { /* noop */ }
  };

  const listHeader = document.createElement("div");
  listHeader.style.cssText = `
    color: #44d3de;
    font-size: 11px;
    font-weight: 600;
    margin: 10px 0 4px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-top: 1px solid rgba(68, 211, 222, 0.2);
    padding-top: 8px;
  `;
  const listTitle = document.createElement("span");
  listTitle.textContent = "Planes";
  const listCounter = document.createElement("span");
  listCounter.style.cssText = "color: #8db5b9; font-size: 10.5px; font-weight: normal;";
  listCounter.textContent = "0";
  listHeader.appendChild(listTitle);
  listHeader.appendChild(listCounter);
  body.appendChild(listHeader);

  const listContainer = document.createElement("div");
  listContainer.style.cssText = `
    display: flex;
    flex-direction: column;
    gap: 3px;
    max-height: 180px;
    overflow-y: auto;
    padding-right: 2px;
  `;
  body.appendChild(listContainer);

  const emptyHint = document.createElement("div");
  emptyHint.style.cssText = "color: #688080; font-size: 10.5px; font-style: italic; padding: 6px 0;";
  emptyHint.textContent = "No planes — double-click a model to create one";
  listContainer.appendChild(emptyHint);

  // planeId → row element so we can update individually
  const rowMap = new Map<string, HTMLElement>();

  // Cheap update of just the colour/opacity dots without rebuilding the
  // whole list. Called whenever the user adjusts the colour or opacity
  // sliders so the row swatches stay in sync.
  const refreshSwatches = () => {
    for (const [planeId, row] of rowMap) {
      const plane: any = clipper.list.get(planeId);
      if (!plane) continue;
      const swatch = row.querySelector("[data-swatch]") as HTMLElement | null;
      if (!swatch) continue;
      const mat = getPlaneMaterial(plane);
      let hex = "#44d3de";
      let op = 1;
      try {
        if (mat?.color) hex = `#${mat.color.getHexString()}`;
        if (typeof mat?.opacity === "number") op = mat.opacity;
      } catch { /* noop */ }
      swatch.style.background = hex;
      swatch.style.opacity = String(Math.max(0.35, op));
      swatch.title = `Color ${hex} · Opacity ${(op * 100).toFixed(0)}%`;
    }
  };

  const buildPlaneRow = (planeId: string, plane: any, index: number) => {
    const row = document.createElement("div");
    row.dataset.planeId = planeId;
    const isSelected = planeId === selectedPlaneId;
    row.style.cssText = `
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 4px 6px;
      background: ${isSelected ? "rgba(68, 211, 222, 0.22)" : "rgba(68, 211, 222, 0.06)"};
      border: 1px solid ${isSelected ? "#44d3de" : "rgba(68, 211, 222, 0.2)"};
      border-radius: 4px;
      font-size: 11px;
      cursor: pointer;
    `;

    // Enable/disable toggle (drives plane.enabled — when false the plane is
    // removed from the renderer's clippingPlanes array, so the model is no
    // longer cut by it but the gizmo & visual mesh stay so the user can re-enable).
    const toggle = document.createElement("input");
    toggle.type = "checkbox";
    toggle.checked = !!plane.enabled;
    toggle.title = "On / off";
    toggle.style.cssText = "accent-color: #44d3de; cursor: pointer; flex-shrink: 0;";
    toggle.addEventListener("change", (e) => {
      e.stopPropagation();
      try {
        plane.enabled = toggle.checked;
        // Hide the visual gizmo when disabled, show when re-enabled.
        plane.visible = toggle.checked;
        refreshFragments();
      } catch (err) {
        console.warn("[clipper] toggle plane.enabled failed", err);
      }
      label.style.opacity = toggle.checked ? "1" : "0.5";
    });

    // Small colour swatch so the user can tell at a glance which plane is
    // which when several have been re-styled. Reads the live material so
    // it always reflects the current colour (including default/shared).
    const swatch = document.createElement("span");
    const mat = getPlaneMaterial(plane);
    let swatchHex = "#44d3de";
    let swatchOp = 1;
    try {
      if (mat?.color) swatchHex = `#${mat.color.getHexString()}`;
      if (typeof mat?.opacity === "number") swatchOp = mat.opacity;
    } catch { /* noop */ }
    swatch.dataset.swatch = "1";
    swatch.title = `Color ${swatchHex} · Opacity ${(swatchOp * 100).toFixed(0)}%`;
    swatch.style.cssText = `
      width: 12px;
      height: 12px;
      border-radius: 3px;
      background: ${swatchHex};
      opacity: ${Math.max(0.35, swatchOp)};
      border: 1px solid rgba(255, 255, 255, 0.35);
      flex-shrink: 0;
      box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.45);
    `;

    const label = document.createElement("span");
    label.textContent = `Plane ${index + 1}`;
    label.style.cssText = "flex: 1; color: #cfeef0; cursor: default;";

    // "Focus" — moves camera so the plane is in view (uses helper position)
    const focusBtn = document.createElement("button");
    focusBtn.title = "Focus camera on this plane";
    focusBtn.innerHTML = `
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="3"/>
        <circle cx="12" cy="12" r="9"/>
      </svg>`;
    focusBtn.style.cssText = `
      background: transparent; border: none; color: #44d3de;
      cursor: pointer; padding: 2px 4px; border-radius: 3px;
      display: flex; align-items: center;
    `;
    focusBtn.onmouseenter = () => { focusBtn.style.background = "rgba(68, 211, 222, 0.18)"; };
    focusBtn.onmouseleave = () => { focusBtn.style.background = "transparent"; };
    focusBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      try {
        const helper = plane.helper as THREE.Object3D;
        if (!helper) return;
        const tgt = helper.position;
        const controls: any = world.camera.controls;
        // Keep camera distance, look at the plane origin
        const camPos = new THREE.Vector3();
        controls.getPosition(camPos);
        controls.setLookAt(camPos.x, camPos.y, camPos.z, tgt.x, tgt.y, tgt.z, true);
      } catch (err) {
        console.warn("[clipper] focus failed", err);
      }
    });

    // Delete this specific plane.
    // We delete from the data map AND defensively clean up the scene/renderer
    // ourselves, then force-rebuild the list. This prevents "phantom" rows
    // when the lib's onAfterDelete event doesn't fire for any reason
    // (orphaned plane, exception during dispose, double-click race, etc).
    const delBtn = document.createElement("button");
    delBtn.title = "Delete this plane";
    delBtn.innerHTML = "&times;";
    delBtn.style.cssText = `
      background: transparent; border: none; color: #ff8a8a;
      cursor: pointer; font-size: 16px; line-height: 1; padding: 0 4px;
      border-radius: 3px; flex-shrink: 0;
    `;
    delBtn.onmouseenter = () => { delBtn.style.background = "rgba(255, 80, 80, 0.18)"; };
    delBtn.onmouseleave = () => { delBtn.style.background = "transparent"; };
    delBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      console.log(`[clipper] delete row clicked planeId=${planeId} listSize=${clipper.list.size}`);
      forceRemovePlane(planeId, plane);
      // Always force-rebuild the list even if events didn't fire
      rebuildList();
      refreshFragments();
    });

    row.appendChild(toggle);
    row.appendChild(swatch);
    row.appendChild(label);
    row.appendChild(focusBtn);
    row.appendChild(delBtn);

    // Row click → toggle selection so the Color/Opacity inputs above
    // target this plane. Children's own click handlers stop propagation
    // already, but we double-check to be safe.
    row.addEventListener("click", (e) => {
      const tgt = e.target as HTMLElement;
      if (
        tgt === toggle || toggle.contains(tgt) ||
        tgt === focusBtn || focusBtn.contains(tgt) ||
        tgt === delBtn || delBtn.contains(tgt)
      ) return;
      selectedPlaneId = selectedPlaneId === planeId ? null : planeId;
      updateScopeLabel();
      syncStyleInputsFromTarget();
      rebuildList();
    });
    return row;
  };

  const rebuildList = () => {
    // If the selection was deleted out from under us, drop it so the
    // style inputs revert to "Default style" automatically.
    if (selectedPlaneId && !clipper.list.has(selectedPlaneId)) {
      selectedPlaneId = null;
      updateScopeLabel();
      syncStyleInputsFromTarget();
    }
    listContainer.innerHTML = "";
    rowMap.clear();
    if (clipper.list.size === 0) {
      listContainer.appendChild(emptyHint);
      listCounter.textContent = "0";
      return;
    }
    let i = 0;
    for (const [planeId, plane] of clipper.list) {
      const row = buildPlaneRow(planeId, plane, i);
      rowMap.set(planeId, row);
      listContainer.appendChild(row);
      i++;
    }
    listCounter.textContent = String(clipper.list.size);
  };

  const onAfterCreateUpdate = () => {
    rebuildList();
    refreshFragments();
  };
  const onAfterDeleteUpdate = () => {
    rebuildList();
    refreshFragments();
  };
  clipper.onAfterCreate.add(onAfterCreateUpdate);
  clipper.onAfterDelete.add(onAfterDeleteUpdate);
  clipper.onAfterDrag.add(refreshFragments);
  rebuildList();
  updateScopeLabel();

  // Mount panel on document.body (not container) so it floats freely and
  // works with `position: fixed`.
  document.body.appendChild(panel);

  // Toggle helpers (concrete impls — replaces the forward stub above)
  const isPanelVisible = () => panel.style.display !== "none";
  setPanelVisible = (visible: boolean) => {
    panel.style.display = visible ? "flex" : "none";
  };
  const togglePanel = () => setPanelVisible(!isPanelVisible());

  return {
    clipper,
    panel,
    togglePanel,
    setPanelVisible,
    isPanelVisible,
    dispose() {
      container.removeEventListener("dblclick", onDblClick);
      if (canvas && canvas !== container) {
        canvas.removeEventListener("dblclick", onDblClick);
      }
      window.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousemove", onPanelDrag);
      document.removeEventListener("mouseup", onPanelDragEnd);
      try { clipper.deleteAll(); } catch { /* noop */ }
      panel.remove();
    },
  };
}
